<template>
  <div class="c-typography-configurator-item flex flex-col gap-2">
    <span class="font-bold">
      {{ typographyPresetTranslations[item.id as keyof typeof typographyPresetTranslations] }}
    </span>
    <CSelect
      bordered
      :options="fontOptions"
      :placeholder="t('canvasUI.CTypographyConfiguratorItem.selectFont')"
      :render-label="renderFontLabel"
      :value="item.font_family"
      @update:value="updateItem('font_family', $event)"
    />
    <div class="flex gap-2">
      <CSelect
        bordered
        class="w-1/2"
        :options="sizeOptions"
        :placeholder="t('canvasUI.CTypographyConfiguratorItem.fontSize.title')"
        :value="item.font_size"
        @update:value="updateItem('font_size', $event)"
      />
      <CSelect
        bordered
        class="w-1/2"
        :options="weightOptions"
        :placeholder="t('canvasUI.CTypographyConfiguratorItem.fontWeight.title')"
        :value="item.font_weight"
        @update:value="updateItem('font_weight', $event)"
      />
    </div>
    <NColorPicker
      :actions="['clear', 'confirm']"
      default-value=""
      :modes="['hex']"
      :on-clear="() => updateItem('color', themeVars.text)"
      :swatches="swatches"
      :value="item.color"
      @update:value="updateItem('color', $event)"
    />
    <CCollapse size="small">
      <CCollapseItem :title="t('canvasUI.CTypographyConfiguratorItem.advancedSettings')">
        <div class="flex flex-col gap-2">
          <div class="flex gap-2">
            <div>
              <CLabelWithHelpText :label="t('canvasUI.CTypographyConfiguratorItem.lineHeightLabel')">
                <template #info>
                  {{ t('canvasUI.CTypographyConfiguratorItem.lineHeightDescription') }}
                </template>
              </CLabelWithHelpText>
              <CInput
                :placeholder="t('canvasUI.CTypographyConfiguratorItem.lineHeightLabel')"
                :style="{ '--n-color': 'transparent' }"
                :value="item.line_height"
                @update:value="updateItem('line_height', $event)"
              />
            </div>
            <div>
              <CLabelWithHelpText :label="t('canvasUI.CTypographyConfiguratorItem.letterSpacingLabel')">
                <template #info>
                  {{ t('canvasUI.CTypographyConfiguratorItem.letterSpacingDescription') }}
                </template>
              </CLabelWithHelpText>
              <CInput
                :placeholder="t('canvasUI.CTypographyConfiguratorItem.letterSpacingLabel')"
                :style="{ '--n-color': 'transparent' }"
                :value="item.letter_spacing"
                @update:value="updateItem('letter_spacing', $event)"
              />
            </div>
          </div>
          <div>
            <CLabelWithHelpText :label="t('canvasUI.CTypographyConfiguratorItem.capitalizationLabel')">
              <template #info>
                {{ t('canvasUI.CTypographyConfiguratorItem.capitalizationDescription') }}
              </template>
            </CLabelWithHelpText>
            <CSelect
              bordered
              label="capitalization"
              :options="capitalizationOptions"
              :value="item.text_transform"
              @update:value="updateItem('text_transform', $event)"
            />
          </div>
        </div>
      </CCollapseItem>
    </CCollapse>
  </div>
</template>

<script setup lang="ts">
import { NColorPicker, type SelectOption } from 'naive-ui'
import { computed, h } from 'vue'
import { useI18n } from 'vue-i18n'

import CCollapse from '@lib/components/CCollapse/CCollapse.vue'
import CCollapseItem from '@lib/components/CCollapse/CCollapseItem.vue'
import CInput from '@lib/components/CInput/CInput.vue'
import CLabelWithHelpText from '@lib/components/CLabelWithHelpText/CLabelWithHelpText.vue'
import CSelect from '@lib/components/CSelect/CSelect.vue'
import useSharedTranslations from '@lib/composables/useSharedTranslations'
import useThemeVars from '@lib/composables/useThemeVars'
import { fontSizePresets } from '@theme/theme.utils'
import { type CanvasTypographyPreset } from '@lib/types/canvasTheme'

const props = defineProps<{
  item: CanvasTypographyPreset
  palette?: string[]
  fontOptions: SelectOption[]
}>()

const emit = defineEmits<{ 'update:item': [i: CanvasTypographyPreset] }>()

const { t } = useI18n()
const { typographyPresetTranslations } = useSharedTranslations()
const themeVars = useThemeVars()

const sizeOptions = fontSizePresets.map((size) => ({
  label: `${size}px`,
  value: `${size}px`,
}))

const weightOptions = [
  { label: t('canvasUI.CTypographyConfiguratorItem.fontWeight.normal'), value: '400' },
  { label: t('canvasUI.CTypographyConfiguratorItem.fontWeight.bold'), value: '700' },
]

const capitalizationOptions = [
  { label: t('canvasUI.CTypographyConfiguratorItem.capitalization.none'), value: 'none' },
  { label: t('canvasUI.CTypographyConfiguratorItem.capitalization.uppercase'), value: 'uppercase' },
  { label: t('canvasUI.CTypographyConfiguratorItem.capitalization.lowercase'), value: 'lowercase' },
  { label: t('canvasUI.CTypographyConfiguratorItem.capitalization.capitalize'), value: 'capitalize' },
]

const renderFontLabel = (option: SelectOption) => {
  return h('span', { style: { fontFamily: option.value } }, option.label?.toString())
}

const swatches = computed(() => {
  const colors = props.palette?.filter((color) => color)
  return colors?.length ? colors : undefined
})

const updateItem = (key: keyof CanvasTypographyPreset, value: any) => {
  emit('update:item', { ...props.item, [key]: value })
}
</script>

<style scoped>
:deep(.c-collapse-item) {
  margin: 0 !important;
  padding: 8px !important;
  background-color: v-bind('themeVars.primary6') !important;
  border-radius: 4px;
}
</style>
